import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  getActivitySummary, getMyBookingsFiltered,
  ActivitySummary, MarketplaceBooking,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showErrorToast } from "../../../atoms/Utlis/Toast";
import { device } from "../../../atoms/Utlis/media";
import {
  Activity, Key, ShoppingBag, MessageSquare, Users,
  TrendingUp, Clock, CheckCircle, XCircle, ArrowRight, Sparkles
} from "lucide-react";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./MarketplaceEmptyState";

const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(201,77,170,0); }
  50% { box-shadow: 0 0 0 8px rgba(201,77,170,0.1); }
`;

type DrillType = "rentals" | "inquiries" | "purchases" | "recruitment" | null;
type BookingDrillType = Exclude<DrillType, null | "recruitment">;

import { STATUS_COLOR, STATUS_LABELS as STATUS_LABEL } from "./marketplaceConstants";

const MarketplaceActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState<DrillType>(null);
  const [drillBookings, setDrillBookings] = useState<MarketplaceBooking[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await getActivitySummary();
      setSummary(res?.data ?? null);
    } catch { setSummary(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, []);

  const openDrill = async (type: DrillType) => {
    if (type === "recruitment") {
      navigate("/dashboard/marketplace/applications");
      return;
    }
    setDrill(type);
    setDrillLoading(true);
    setDrillBookings([]);
    try {
      const params =
        type === "rentals" ? { listing_type: "rent" as const } :
        type === "inquiries" ? { booking_type: "inquiry" as const } :
        type === "purchases" ? { listing_type: "sale" as const } :
        {};
      const res = await getMyBookingsFiltered({ ...params, page_size: 50 });
      setDrillBookings(res.results ?? []);
    } catch {
      showErrorToast("Failed to load bookings");
    } finally {
      setDrillLoading(false);
    }
  };

  const widgets = summary ? [
    {
      key: "rentals" as DrillType,
      label: "Rentals",
      icon: <Key size={22} />,
      badge: summary.badges.rentals,
      color: "#7c4dff",
      rows: [
        { label: "Active", value: summary.rentals.active, color: "#16a34a" },
        { label: "Completed", value: summary.rentals.completed, color: "#6b7280" },
        { label: "Cancelled", value: summary.rentals.cancelled, color: "#ef4444" },
        { label: "Total", value: summary.rentals.total, color: "#171717" },
      ],
    },
    {
      key: "inquiries" as DrillType,
      label: "Inquiries",
      icon: <MessageSquare size={22} />,
      badge: summary.badges.inquiries,
      color: "#f09a3e",
      rows: [
        { label: "Pending", value: summary.inquiries.pending, color: "#f09a3e" },
        { label: "Accepted", value: summary.inquiries.accepted, color: "#16a34a" },
        { label: "Rejected", value: summary.inquiries.rejected, color: "#ef4444" },
        { label: "Total", value: summary.inquiries.total, color: "#171717" },
      ],
    },
    {
      key: "purchases" as DrillType,
      label: "Purchases",
      icon: <ShoppingBag size={22} />,
      badge: summary.badges.purchases,
      color: "#0ea5e9",
      rows: [
        { label: "Confirmed", value: summary.purchases.confirmed, color: "#16a34a" },
        { label: "Completed", value: summary.purchases.completed, color: "#6b7280" },
        { label: "Pending Payment", value: summary.purchases.pending_payment, color: "#f09a3e" },
        { label: "Total", value: summary.purchases.total, color: "#171717" },
      ],
    },
    {
      key: "recruitment" as DrillType,
      label: "Recruitment",
      icon: <Users size={22} />,
      badge: summary.badges.recruitment,
      color: "#c94daa",
      rows: [
        { label: "Submitted", value: summary.recruitment.applications_submitted.total, color: "#171717" },
        { label: "Pending", value: summary.recruitment.applications_submitted.pending, color: "#f09a3e" },
        { label: "Accepted", value: summary.recruitment.applications_submitted.accepted, color: "#16a34a" },
        { label: "Received", value: summary.recruitment.applications_received.total, color: "#7c4dff" },
      ],
    },
  ] : [];

  const drillEmptyConfig: Record<BookingDrillType, {
    icon: React.ReactNode;
    title: string;
    description: string;
    hint: string;
  }> = {
    rentals: {
      icon: <Key size={36} strokeWidth={1.5} />,
      title: "No rentals yet",
      description: "Equipment rental bookings will show up here once customers reserve your gear.",
      hint: "List equipment for rent to start getting rental requests",
    },
    inquiries: {
      icon: <MessageSquare size={36} strokeWidth={1.5} />,
      title: "No inquiries yet",
      description: "Customer inquiries about your listings will appear here when they reach out.",
      hint: "Active listings attract more inquiries from the community",
    },
    purchases: {
      icon: <ShoppingBag size={36} strokeWidth={1.5} />,
      title: "No purchases yet",
      description: "Sale bookings and purchase requests will appear here when buyers show interest.",
      hint: "List items for sale to receive purchase requests",
    },
  };

  const activeBookingDrill = drill && drill !== "recruitment" ? drill : null;

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <TitleIcon><Activity size={22} /></TitleIcon>
          <div>
            <Title>Activity Summary</Title>
            <Subtitle>Your marketplace activity at a glance</Subtitle>
          </div>
        </HeaderLeft>
      </Header>

      {loading ? (
        <MarketplaceLoadingSpinner />
      ) : !summary ? (
        <EmptyState>No activity data found. Start buying, renting, or applying!</EmptyState>
      ) : !drill ? (
        <WidgetGrid>
          {widgets.map(w => (
            <Widget key={w.key} $color={w.color} onClick={() => openDrill(w.key)}>
              <WidgetContent>
                <WidgetTop>
                  <WidgetIcon $color={w.color}>{w.icon}</WidgetIcon>
                  {w.badge > 0 && <WidgetBadge>{w.badge} new</WidgetBadge>}
                </WidgetTop>
                <WidgetLabel>{w.label}</WidgetLabel>
                <WidgetRows>
                  {w.rows.map(r => (
                    <WidgetRow key={r.label}>
                      <WidgetRowLabel>{r.label}</WidgetRowLabel>
                      <WidgetRowValue $color={r.color}>{r.value}</WidgetRowValue>
                    </WidgetRow>
                  ))}
                </WidgetRows>
              </WidgetContent>
              <WidgetFooter><span>View details</span> <ArrowRight size={14} /></WidgetFooter>
            </Widget>
          ))}
        </WidgetGrid>
      ) : (
        <DrillPanel>
          <DrillHeader>
            <DrillTitle>
              {drill === "rentals" ? "My Rentals" : drill === "inquiries" ? "My Inquiries" : drill === "purchases" ? "My Purchases" : "Recruitment"}
            </DrillTitle>
            <CloseBtn onClick={() => setDrill(null)}>✕ Close</CloseBtn>
          </DrillHeader>

          {drillLoading ? (
            <MarketplaceLoadingSpinner padding="48px 20px" />
          ) : drillBookings.length === 0 && activeBookingDrill ? (
            <MarketplaceEmptyState
              padding="32px 20px 48px"
              icon={drillEmptyConfig[activeBookingDrill].icon}
              title={drillEmptyConfig[activeBookingDrill].title}
              description={drillEmptyConfig[activeBookingDrill].description}
              actions={[
                { label: "Back to Activity", onClick: () => setDrill(null), variant: "secondary" },
                { label: "Manage Listings", onClick: () => navigate("/dashboard/marketplace/listings"), icon: <ArrowRight size={15} /> },
              ]}
              hint={{ icon: <Sparkles size={13} />, text: drillEmptyConfig[activeBookingDrill].hint }}
            />
          ) : (
            <DrillList>
              {drillBookings.map(b => (
                <DrillCard key={b.id}>
                  <DrillCardTop>
                    <DrillCardTitle>{b.listing_title ?? "Booking"}</DrillCardTitle>
                    <DrillStatus $color={STATUS_COLOR[b.status] ?? "#6b7280"}>
                      {STATUS_LABEL[b.status] ?? b.status}
                    </DrillStatus>
                  </DrillCardTop>
                  <DrillCardMeta>
                    {b.date_from && (
                      <MetaItem><Clock size={11} /> {b.date_from}{b.date_to ? ` → ${b.date_to}` : ""}</MetaItem>
                    )}
                    <MetaItem>
                      {b.status === "accepted" ? (
                        <CheckCircle size={11} color="#16a34a" />
                      ) : b.status === "rejected" || b.status === "cancelled" ? (
                        <XCircle size={11} color="#ef4444" />
                      ) : null}
                      ₹{Number(b.amount_due).toLocaleString("en-IN")}
                    </MetaItem>
                    <MetaItem>Ref: {b.reference}</MetaItem>
                  </DrillCardMeta>
                </DrillCard>
              ))}
            </DrillList>
          )}
        </DrillPanel>
      )}
    </PageContainer>
  );
};

export default MarketplaceActivityPage;



const PageContainer = styled.div`
  width: 100%;
  min-height: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  animation: ${fadeUp} 0.3s ease;
  @media ${device.tablet} { padding: 16px; }
`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 28px;
`;

const HeaderLeft = styled.div`display: flex; align-items: center; gap: 12px;`;

const TitleIcon = styled.div`
  width: 44px; height: 44px; border-radius: 12px;
  background: ${gradient};
  display: flex; align-items: center; justify-content: center; color: white;
`;

const Title = styled.h1`font-size: 20px; font-weight: 800; color: #171717; margin: 0;`;
const Subtitle = styled.p`font-size: 13px; color: rgba(23,23,23,0.5); margin: 0; font-family: Montserrat, sans-serif;`;

const EmptyState = styled.p`
  text-align: center; padding: 40px 0;
  font-family: Montserrat, sans-serif; font-size: 14px; color: rgba(23,23,23,0.35);
`;

const WidgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  @media ${device.tablet} { grid-template-columns: 1fr; }
`;

const Widget = styled.div<{ $color: string }>`
  background: white;
  border: 1px solid rgba(23,23,23,0.07);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.25s;
  box-shadow: 0 4px 16px rgba(23,23,23,0.03);
  animation: ${fadeUp} 0.3s ease;
  overflow: hidden;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 28px ${p => p.$color}22;
    border-color: ${p => p.$color}44;
  }
`;

const WidgetContent = styled.div`
  padding: 24px;
  flex: 1;
`;

const WidgetTop = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;`;

const WidgetIcon = styled.div<{ $color: string }>`
  width: 42px; height: 42px; border-radius: 11px;
  background: ${p => p.$color}18;
  color: ${p => p.$color};
  display: flex; align-items: center; justify-content: center;
`;

const WidgetBadge = styled.span`
  background: ${gradient}; color: white;
  font-size: 10px; font-weight: 700; font-family: Montserrat, sans-serif;
  border-radius: 100px; padding: 2px 8px;
`;

const WidgetLabel = styled.div`font-size: 18px; font-weight: 800; color: #171717; margin-bottom: 16px;`;

const WidgetRows = styled.div`display: flex; flex-direction: column; gap: 10px;`;

const WidgetRow = styled.div`display: flex; align-items: center; justify-content: space-between;`;

const WidgetRowLabel = styled.span`font-family: Montserrat, sans-serif; font-size: 12px; color: rgba(23,23,23,0.5);`;

const WidgetRowValue = styled.span<{ $color: string }>`
  font-family: Montserrat, sans-serif; font-size: 13px; font-weight: 700;
  color: ${p => p.$color};
`;

const WidgetFooter = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 14px;
  border-top: 1px solid rgba(23,23,23,0.06);
  background: #fafafa;
  font-family: Urbanist, sans-serif; font-size: 13px; font-weight: 700;
  color: #c94daa;
  transition: background 0.2s;
  
  ${Widget}:hover & {
    background: #f0f0f0;
  }
`;

const DrillPanel = styled.div`
  margin-top: 0;
  background: white;
  border: 1px solid rgba(23,23,23,0.07);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(23,23,23,0.04);
  animation: ${fadeUp} 0.3s ease;
`;

const DrillHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(23,23,23,0.07);
`;

const DrillTitle = styled.h2`font-size: 16px; font-weight: 800; color: #171717; margin: 0;`;

const CloseBtn = styled.button`
  border: none; background: transparent;
  font-family: Montserrat, sans-serif; font-size: 12px; font-weight: 600;
  color: rgba(23,23,23,0.4); cursor: pointer;
  &:hover { color: #ef4444; }
`;

const DrillList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  padding: 20px;
`;

const DrillCard = styled.div`
  padding: 16px 20px;
  background: white;
  border: 1px solid rgba(23,23,23,0.07);
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(23,23,23,0.03);
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(23,23,23,0.06);
  }
`;

const DrillCardTop = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;`;

const DrillCardTitle = styled.div`font-size: 14px; font-weight: 700; color: #171717;`;

const DrillStatus = styled.span<{ $color: string }>`
  padding: 3px 10px; border-radius: 100px;
  font-size: 11px; font-weight: 700; font-family: Montserrat, sans-serif;
  background: ${p => p.$color}18; color: ${p => p.$color};
`;

const DrillCardMeta = styled.div`display: flex; align-items: center; gap: 14px; flex-wrap: wrap;`;

const MetaItem = styled.div`
  display: flex; align-items: center; gap: 4px;
  font-family: Montserrat, sans-serif; font-size: 11px; color: rgba(23,23,23,0.5);
`;
